import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { listQuestions, deleteQuestion } from "../../../services/QuestionsService"
import Loader from "../Common/Loader"

const Questions = () => {
  const navigate = useNavigate()
  const [allQuestions, setAllQuestions] = useState([])
  const [numRows, setNumRows] = useState(0)
  const [searchQueries, setSearchQueries] = useState({
    Question: "",
    question_type: "",
    is_required: "",
  })
  const [sortField, setSortField] = useState("Question")
  const [sortOrder, setSortOrder] = useState("asc")
  const [currentPage, setCurrentPage] = useState(0)
  const rowsPerPage = 10
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [activeSearchField, setActiveSearchField] = useState("")
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("")

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true)
      setError("")
      try {
        if (location.state?.returnTo) {
          const { currentPage, searchQueries, sortField, sortOrder } = location.state.returnTo
          setCurrentPage(currentPage || 0)
          setSearchQueries(searchQueries || {})
          setSortField(sortField || "Question")
          setSortOrder(sortOrder || "asc")
        }
        await fetchQuestionsFromServer()
      } catch (err) {
        console.error("Failed to fetch questions:", err)
        setError("Failed to load questions. Please try again.")
      } finally {
        setLoading(false)
      }
    }
    fetchQuestions()
  }, [])

  const fetchQuestionsFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      const admin_id = JSON.parse(localStorage.getItem("loginDetails"))?.id || 1
      const payload = {
        admin_id,
        search_by,
        search_keyword,
        sort_by,
        sort_order,
        pagination,
        pageid,
      }
      const res = await listQuestions(payload)
      if (res.data.status === "success") {
        const mapped = res.data.data.map((q) => ({
          Question: q.question,
          question_type: q.question_type,
          is_required: q.is_required,
          id: q.question_id,
          raw: q,
        }))
        setAllQuestions(mapped)
        setNumRows(res.data.num_rows || 0)
      } else {
        setError("Failed to fetch questions")
      }
    } catch (err) {
      console.error("Failed to fetch questions:", err)
      setError("Failed to load questions.")
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    fetchQuestionsFromServer(
      activeSearchField,
      activeSearchKeyword,
      true,
      page,
      sortField,
      sortOrder
    )
  }

  // Modified handleSearch to clear other fields
  const handleSearch = (e, field) => {
    const keyword = e.target.value
    
    // Clear other search fields when typing in current field
    const clearedQueries = {
      Question: "",
      question_type: "",
      is_required: "",
    }
    
    const updatedQueries = { ...clearedQueries, [field]: keyword }
    setSearchQueries(updatedQueries)
    setCurrentPage(0)
    
    // Track active search
    setActiveSearchField(field)
    setActiveSearchKeyword(keyword)
    fetchQuestionsFromServer(field, keyword, true, 0, sortField, sortOrder)
  }

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc"
    setSortField(field)
    setSortOrder(order)
    setCurrentPage(0)
    fetchQuestionsFromServer(activeSearchField, activeSearchKeyword, true, 0, field, order)
  }

  const handleEdit = (question) => {
    navigate(`/edit-question/${question.id}`, {
      state: {
        question,
        returnTo: {
          currentPage,
          searchQueries,
          sortField,
          sortOrder,
        },
      },
    })
  }

  const handleDelete = (question) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this question!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
      customClass: {
        confirmButton: "btn btn-white",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true)
          const admin_id = JSON.parse(localStorage.getItem("loginDetails"))?.id || 1
          await deleteQuestion({ admin_id, question_id: question.id })
          Swal.fire("Deleted!", "Your question has been deleted.", "success")
          await fetchQuestionsFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          )
        } catch (err) {
          setError("Failed to delete question")
          Swal.fire("Error", err?.response?.data?.messages?.error || "Error deleting question", "error")
          console.error("Delete failed:", err)
        } finally {
          setLoading(false)
        }
      } else {
        Swal.fire("Cancelled", "Your question is safe :)", "info")
      }
    })
  }

  const paginatedData = allQuestions
  const totalPages = Math.ceil(numRows / rowsPerPage)

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link
            to="/add-question"
            state={{
              returnTo: {
                currentPage,
                searchQueries,
                sortField,
                sortOrder,
              },
            }}
            className="btn btn-primary btn-rounded"
          >
            + Add Question
          </Link>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th 
                            onClick={() => handleSort("Question")} 
                            style={{ cursor: "pointer", minWidth: "300px", verticalAlign: "top" }}
                            className="position-relative"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span>Question   <span>{sortField === "Question" && (sortOrder === "asc" ? "↑" : "↓")}</span></span>
                            
                            </div>
                            <input
                              className="form-control form-control-sm"
                              value={searchQueries.Question}
                              onChange={(e) => handleSearch(e, "Question")}
                              placeholder="Search Question"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th 
                            onClick={() => handleSort("question_type")} 
                            style={{ cursor: "pointer", minWidth: "200px", verticalAlign: "top" }}
                            className="position-relative"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span>Selective Type  <span>{sortField === "question_type" && (sortOrder === "asc" ? "↑" : "↓")}</span></span>
                             
                            </div>
                            <input
                              className="form-control form-control-sm"
                              value={searchQueries.question_type}
                              onChange={(e) => handleSearch(e, "question_type")}
                              placeholder="Search Type"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th 
                            onClick={() => handleSort("is_required")} 
                            style={{ cursor: "pointer", minWidth: "150px", verticalAlign: "top" }}
                            className="position-relative"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span>Required  <span>{sortField === "is_required" && (sortOrder === "asc" ? "↑" : "↓")}</span></span>
                             
                            </div>
                            <input
                              className="form-control form-control-sm"
                              value={searchQueries.is_required}
                              onChange={(e) => handleSearch(e, "is_required")}
                              placeholder="Search Required"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th style={{ minWidth: "120px", verticalAlign: "top" }}>
                            <div className="mb-2">Actions</div>
                            <div style={{ height: "31px" }}></div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((question) => (
                          <tr key={question.id}>
                            <td className="align-middle">{question.Question}</td>
                            <td className="align-middle">{question.question_type}</td>
                            <td className="align-middle">{question.is_required}</td>
                            <td className="align-middle">
                              <div className="d-flex">
                                <span
                                  className="edit me-4"
                                  onClick={() => handleEdit(question)}
                                  style={{ cursor: "pointer" }}
                                  title="Edit Question"
                                >
                                  <i className="fa fa-pencil-alt text-success"></i>
                                </span>
                                <span
                                  className="delete me-2"
                                  onClick={() => handleDelete(question)}
                                  style={{ cursor: "pointer" }}
                                  title="Delete Question"
                                >
                                  <i className="fa fa-trash text-danger"></i>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {paginatedData.length === 0 && (
                          <tr>
                            <td colSpan="4" className="text-center">
                              No questions found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {allQuestions?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                      {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = []
                        const maxPagesToShow = 5
                        const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow))
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages)
                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          )
                          if (startPage > 1) {
                            pages.push(
                              <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                                ...
                              </span>
                            )
                          }
                        }
                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          )
                        }
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                                ...
                              </span>
                            )
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          )
                        }
                        return pages
                      })()}
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Questions
